import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useCartStore } from '@astro/store'
import { useAuth } from '@astro/auth'
import { api } from '@astro/api'
import { useToast } from '../../context/ToastContext.jsx'
import { useWishlist } from '../../hooks/useWishlist.js'
import { currencyFormatter } from './helper.js'

const stripHtml = (html) => (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

// Accepts either a live API product (name, slug, image_url, …) or the old
// hardcoded shape (title, img, priceLabel, …) so both paths keep working.
export default function ProductCard({ product: p }) {
  const add = useCartStore((s) => s.add)
  const { showToast } = useToast()
  const { status, login } = useAuth()
  const queryClient = useQueryClient()

  // Wishlist is a signed-in-only, server-persisted feature — the icon state
  // comes straight from the last confirmed ['wishlist'] fetch, never from
  // the click itself, so it only ever changes once the API confirms it.
  const { has: isWishlisted, toggle: toggleWishlist } = useWishlist()
  const wishlisted = isWishlisted(p.id)
  const [togglingWishlist, setTogglingWishlist] = useState(false)

  // ── field normalisation ─────────────────────────────────────────────────
  const isApiProduct = Boolean(p.slug)           // live API always has slug
  const title = isApiProduct ? p.name : p.title
  const img = isApiProduct ? p.image_url : p.img
  // const slug = isApiProduct ? p.slug : p.id
  const slug = p.id
  const desc = isApiProduct ? stripHtml(p.sort_description) : p.desc
  const displayPrice = isApiProduct ? currencyFormatter(p.total_price) : p.priceLabel
  // /product/search sends has_discount + discount_amount; the other product
  // lists only carry discount_rate, so derive both from it there.
  const discountRate = parseFloat(p.discount_rate) || 0
  const hasDiscount = isApiProduct && (p.has_discount ?? discountRate > 0)
  const listPrice = parseFloat(p.product_price ?? p.price)
  const origPrice = hasDiscount ? currencyFormatter(listPrice) : null
  const savings = hasDiscount
    ? currencyFormatter(p.discount_amount != null ? parseFloat(p.discount_amount) : (listPrice * discountRate) / 100)
    : null
  const inStock = isApiProduct ? parseInt(p.stock) > 0 : true
  const category = isApiProduct
    ? (p.categories?.find(c => c.parent_id === null)?.name || p.categories?.[0]?.name || '')
    : p.category

  const handleAdd = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!inStock) {
      showToast(`${title} is out of stock`)
      return
    }

    // Guests can add freely — login is only enforced at checkout (RequireAuth
    // on /checkout already handles that). Only sync to the server cart once
    // there's a session to attach it to; legacy hardcoded demo products have
    // no real backend record to add to either way.
    add({
      productId: p.id,
      title,
      // Cart amounts are paise; API prices are rupee strings, legacy p.price is already paise
      price: { amount: isApiProduct ? Math.round(parseFloat(p.total_price) * 100) : p.price, currency: 'INR' },
      qty: 1,
    })
    showToast(`${title} added to basket`)

    if (isApiProduct && status === 'authenticated') {
      api.cart.addToCart({ product_id: p.id, quantity: 1 })
        .then(() => queryClient.invalidateQueries({ queryKey: ['cart'] }))
        .catch(() => {
          showToast('Could not sync your basket with the server — it will retry at checkout.')
        })
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (status !== 'authenticated') {
      showToast('Sign in to save items to your wishlist')
      // login()
      return
    }
    if (togglingWishlist) return

    setTogglingWishlist(true)
    try {
      await toggleWishlist(p.id, wishlisted)
      showToast(wishlisted ? `${title} removed from wishlist` : `${title} added to wishlist`)
    } catch (err) {
      showToast(err?.message || 'Could not update your wishlist — try again.')
    } finally {
      setTogglingWishlist(false)
    }
  };

  return (
    <Link
      to={`/products/${slug}`}
      className="group relative bg-white border border-gray-100 rounded-3xl overflow-hidden transition-all duration-500 hover:border-secondary/40 hover:shadow-2xl hover:shadow-primary/5 flex flex-col h-full font-normal uppercase tracking-tight outline-none"
    >
      {/* IMAGE */}
      <div className="relative aspect-square overflow-hidden bg-muted/10 border-b border-gray-100 flex items-center justify-center">
        {/* Wishlist toggle — needs a real backend product id, so hidden for legacy demo cards */}
        {isApiProduct && (
          <button
            type="button"
            onClick={handleToggleWishlist}
            disabled={togglingWishlist}
            title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            className="absolute top-3 left-3 z-30 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center transition-all active:scale-90 text-primary/60 disabled:opacity-60"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill={wishlisted ? '#e6392f' : 'none'} stroke={wishlisted ? '#e6392f' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
            </svg>
          </button>
        )}

        <img
          alt={title}
          loading="lazy"
          className="object-contain w-3/4 h-3/4 transition-transform duration-1000 group-hover:scale-110"
          src={img}
        />

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-3 z-20">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!inStock}
            className="inline-flex items-center justify-center px-6 py-2 w-48 h-11 rounded-xl font-semibold text-[9px] tracking-widest bg-white text-primary hover:bg-secondary hover:text-white border-none transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-primary"
          >
            {inStock ? (
              <>ADD TO BASKET <i className="fa-solid fa-shopping-bag ml-2"></i></>
            ) : (
              <>OUT OF STOCK <i className="fa-solid fa-ban ml-2"></i></>
            )}
          </button>
        </div>

        {/* Discount badge */}
        {hasDiscount && (
          <div className="absolute top-4 right-4 z-10">
            <span className="bg-secondary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">
              {parseFloat(p.discount_rate)}% OFF
            </span>
          </div>
        )}

        {/* Category badge */}
        {category && (
          <div className="absolute bottom-4 left-4 z-10">
            <span className="bg-primary text-white px-3 py-1 rounded-lg text-[8px] font-bold tracking-widest border border-white/10 uppercase shadow-lg">
              {category}
            </span>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="p-6 flex flex-col flex-1 space-y-4">
        {/* Rating & lab verified */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 text-secondary text-[10px]">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className="fa-solid fa-star"></i>
            ))}
          </div>
          <div className="flex items-center gap-1.5 opacity-40 text-primary">
            <i className="fa-solid fa-shield-halved text-[10px] text-secondary"></i>
            <span className="text-[8px] font-bold tracking-widest uppercase">LAB VERIFIED</span>
          </div>
        </div>

        {/* Name & description */}
        <div className="space-y-2 flex-1">
          <h3 className="text-base font-heading font-semibold text-primary uppercase leading-tight line-clamp-1">
            {title}
          </h3>
          <p className="text-[11px] text-primary/50 font-body leading-relaxed line-clamp-3 normal-case">
            {desc}
          </p>
        </div>

        {/* Price & stock */}
        <div className="pt-4 border-t border-dashed border-gray-100 flex items-center justify-between mt-auto">
          <div className="flex flex-col">
            <span className="text-[8px] text-primary/30 uppercase tracking-[0.2em] mb-1">SYSTEM PRICE</span>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-primary">{displayPrice}</span>
              {origPrice && <span className="text-xs text-primary/30 line-through">{origPrice}</span>}
            </div>
            {savings && (
              <span className="mt-1 text-[8px] font-bold tracking-widest text-green-600 uppercase">
                YOU SAVE {savings}
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 ${inStock ? 'text-green-600' : 'text-red-400'}`}>
            <span className="text-[8px] font-bold tracking-widest uppercase opacity-60">
              {inStock ? 'AVAILABLE' : 'OUT OF STOCK'}
            </span>
            <div className={`h-1.5 w-1.5 rounded-full ${inStock ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`}></div>
          </div>
        </div>
      </div>
    </Link>
  )
}
